import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, BookOpen } from "lucide-react";
import MuffinRating from "../MuffinRating";
import { REVIEWS } from "@/data";

export default function Recensioni() {
    const [open, setOpen] = useState(null);

    return (
        <div>
            <p className="f-body italic text-[#6d5638] text-center mb-8 text-base" data-testid="recensioni-intro">
                Gli abstract dei libri letti: clicca su una scheda per aprire la recensione completa.
            </p>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="reviews-grid">
                {REVIEWS.map((r, i) => (
                    <motion.button
                        key={r.id}
                        type="button"
                        onClick={() => setOpen(r)}
                        className="parchment-card p-6 text-left flex flex-col cursor-pointer"
                        initial={{ opacity: 0, y: 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.15 }}
                        transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                        whileHover={{ y: -4 }}
                        data-testid={`review-card-${r.id}`}
                    >
                        <span className="f-detail text-5xl leading-none text-[#c1603f]" aria-hidden="true">
                            “
                        </span>
                        <p className="f-body text-[#43301c] text-base sm:text-[17px] leading-relaxed -mt-2">{r.abstract}</p>
                        <div className="mt-auto pt-5 mt-5 border-t border-dashed border-[#8a683c]/40">
                            <div className="flex items-center justify-between gap-3">
                                <cite className="f-title not-italic text-[0.66rem] tracking-[0.16em] uppercase text-[#7a5a2e]">
                                    {r.book}
                                </cite>
                                <MuffinRating value={r.rating} testid={`review-muffin-${r.id}`} />
                            </div>
                            <span className="f-title text-[0.64rem] tracking-[0.16em] uppercase text-[#9c4a2e] inline-flex items-center gap-1.5 mt-3">
                                <BookOpen size={13} /> Leggi la recensione completa
                            </span>
                        </div>
                    </motion.button>
                ))}
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
                        style={{ background: "rgba(23, 12, 6, 0.74)", backdropFilter: "blur(3px)" }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setOpen(null)}
                        data-testid="review-modal-overlay"
                    >
                        <motion.div
                            className="relative w-full max-w-2xl max-h-[84vh] overflow-y-auto rounded-2xl"
                            style={{
                                backgroundColor: "#f6ecd2",
                                backgroundImage: "linear-gradient(180deg, rgba(250,243,226,0.86), rgba(249,240,218,0.88)), url('/cards/parchment.jpg')",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                border: "2px solid rgba(176, 141, 87, 0.8)",
                                boxShadow: "0 40px 90px rgba(0, 0, 0, 0.6), inset 0 0 40px rgba(140, 100, 60, 0.16)",
                            }}
                            initial={{ opacity: 0, scale: 0.94, rotateY: -12 }}
                            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                            exit={{ opacity: 0, scale: 0.96, rotateY: 10 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            data-lenis-prevent
                            data-testid="review-modal"
                        >
                            <button
                                type="button"
                                onClick={() => setOpen(null)}
                                aria-label="Chiudi la recensione"
                                className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#b08d57]/70 bg-[rgba(255,246,224,0.75)] text-[#7a5a2e] flex items-center justify-center hover:bg-[rgba(176,141,87,0.2)] transition-colors cursor-pointer"
                                data-testid="review-modal-close"
                            >
                                <X size={17} />
                            </button>

                            <div className="p-7 sm:p-10">
                                <p className="chapter-eyebrow">Recensione completa</p>
                                <h3 className="chapter-title mt-2" style={{ fontSize: "clamp(1.5rem, 3.4vw, 2.1rem)" }}>
                                    {open.book}
                                </h3>
                                <p className="f-body italic text-[#6d5638] mt-1 text-base">{open.by}</p>
                                <div className="flex items-center gap-3 mt-4">
                                    <MuffinRating value={open.rating} testid="review-modal-muffin" />
                                </div>
                                <div className="flex items-center gap-3 mt-5 mb-6" aria-hidden="true">
                                    <span className="block h-px w-16 bg-gradient-to-r from-transparent to-[#b08d57]" />
                                    <span className="block w-2 h-2 rotate-45 bg-[#b08d57]" />
                                    <span className="block h-px w-16 bg-gradient-to-l from-transparent to-[#b08d57]" />
                                </div>
                                <div className="flex flex-col gap-4">
                                    {open.paragraphs.map((p, n) => (
                                        <p key={n} className="f-body text-[#38281a] text-[17px] leading-relaxed">
                                            {p}
                                        </p>
                                    ))}
                                </div>
                                <p className="f-detail italic text-sm text-[#8a6d4a] mt-8">
                                    Tea &amp; Tales Book Club — dalla discussione dell'incontro nasce questa recensione.
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
